import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Question, ResponsePayload } from 'types';
import { answerReferences, type AnswerReferenceSource } from 'services/api/answerReferencesClient';
import { ANSWER_REFERENCE_TIMEOUT_MS, type AnswerReferenceContextValue, type AnswerReferenceMap } from 'utils/answerReferences';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { extractAnswerReferenceKeys } from 'utils/questionMarkdown';
import { normalizeQuestionType } from 'utils/questionTypeUtils';

/** Keys in the learner-visible authored fields; personalized wording replaces `text`. */
export const questionAnswerReferenceKeys = (question?: Question | null): string[] => {
  if (!question) return [];
  if (normalizeQuestionType(question.question_type_display ?? question.question_type ?? '') === 'roleplay') return [];
  const fields = [question.header, hasQuestionGeneration(question) ? null : question.text, question.subtext];
  return extractAnswerReferenceKeys(fields);
};

export const hasAnswerReferences = (question?: Question | null): boolean =>
  questionAnswerReferenceKeys(question).length > 0;

// A cheap signature of the other questions' completed answers. Saving or editing
// an earlier answer refreshes references; typing in the current answer does not.
const completedAnswerSignature = (
  responses: Record<number, ResponsePayload | undefined>,
  activeQuestionId: number | undefined,
  persistedRevisions: Record<number, number>,
): string => {
  let hash = 5381;
  Object.entries(responses).sort(([a], [b]) => Number(a) - Number(b)).forEach(([questionId, response]) => {
    if (!response || response.is_completed !== true || Number(questionId) === activeQuestionId) return;
    const value = JSON.stringify([questionId, persistedRevisions[Number(questionId)] ?? 0,
      (response as { responseId?: number }).responseId, response.rating_value,
      response.boolean_value, response.text_value, response.text_list, response.selected_options,
      response.custom_other_value, response.audio_url]);
    for (let index = 0; index < value.length; index += 1) hash = ((hash << 5) + hash + value.charCodeAt(index)) | 0;
  });
  return String(hash >>> 0);
};

const NO_REVISIONS: Record<number, number> = {};

interface Params {
  question: Question | null;
  flowName: string;
  flowItemId?: number;
  source: AnswerReferenceSource | null;
  responses: Record<number, ResponsePayload | undefined>;
  enabled: boolean;
  /** Earlier answers are still being saved; wait so the server reads them. */
  savesPending?: boolean;
  /** Completed saves per question, so an edit refreshes once it is persisted. */
  persistedRevisions?: Record<number, number>;
}

/**
 * Resolve REQ-106 references for the active item. Values are fetched when the
 * item becomes active (after earlier answers are saved) and kept while a newer
 * request revalidates, so the card never flashes between states.
 */
export const useAnswerReferences = ({
  question, flowName, flowItemId, source, responses, enabled, savesPending = false, persistedRevisions = NO_REVISIONS,
}: Params) => {
  const keys = useMemo(() => questionAnswerReferenceKeys(question), [question]);
  const required = enabled && keys.length > 0;
  const sourceKey = source ? ('flowRunId' in source ? `run:${source.flowRunId}` : `quiz:${source.quizAttemptId}`) : null;
  const baseKey = required ? `${flowName}:${sourceKey}:${flowItemId}:${question?.id}:${question?.updated_at ?? ''}` : null;
  const signature = useMemo(
    () => completedAnswerSignature(responses, question?.id, persistedRevisions),
    [responses, question?.id, persistedRevisions],
  );
  const requestKey = baseKey ? `${baseKey}:${signature}` : null;
  const [state, setState] = useState<{
    baseKey: string; requestKey: string; activation: number; references?: AnswerReferenceMap; digest?: string; failed?: boolean;
  } | null>(null);
  // Each time an item becomes active again it is revalidated, even if its
  // signature is unchanged (another tab may have changed a quoted answer).
  const activationRef = useRef({ baseKey: null as string | null, count: 0 });
  if (activationRef.current.baseKey !== baseKey) {
    activationRef.current = { baseKey, count: activationRef.current.count + 1 };
  }
  const [refreshCount, setRefreshCount] = useState(0);
  const activation = activationRef.current.count * 1000 + refreshCount;
  const refresh = useCallback(() => setRefreshCount((value) => value + 1), []);
  const activationValueRef = useRef(activation);
  activationValueRef.current = activation;

  useEffect(() => {
    if (!baseKey || !requestKey || savesPending) return;
    if (state?.requestKey === requestKey && state.activation === activation && state.references && !state.failed) return;
    let active = true;
    if (!source || !flowItemId) {
      setState({ baseKey, requestKey, activation, failed: true });
      return;
    }
    answerReferences.get(flowName, flowItemId, source)
      .then(({ references, digest }) => { if (active) setState({ baseKey, requestKey, activation, references, digest }); })
      .catch(() => {
        // Never block the learner: unresolved references read as neutral text.
        // Keep the values still on screen together with the digest they came with.
        if (active) setState((previous) => ({
          baseKey, requestKey, activation, failed: true,
          references: previous?.baseKey === baseKey ? previous.references : undefined,
          digest: previous?.baseKey === baseKey ? previous.digest : undefined,
        }));
      });
    return () => { active = false; };
    // `source` is represented by `sourceKey` inside the request keys; `state` is
    // read only to skip an identical completed request after saves settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey, requestKey, activation, flowName, flowItemId, savesPending]);

  // Bound the whole wait, including pending saves, with one deadline per active
  // item: later saves or revisions never extend it. Afterwards neutral wording
  // shows and a later successful request still replaces it.
  const requestKeyRef = useRef(requestKey);
  requestKeyRef.current = requestKey;
  useEffect(() => {
    if (!baseKey) return;
    const timer = setTimeout(() => {
      setState((previous) => (previous?.baseKey === baseKey
        ? previous : { baseKey, requestKey: requestKeyRef.current ?? baseKey, activation: activationValueRef.current, failed: true }));
    }, ANSWER_REFERENCE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [baseKey]);

  const current = state?.baseKey === baseKey ? state : null;
  const context = useMemo<AnswerReferenceContextValue | null>(() => (
    required ? { mode: 'runtime', references: current?.references ?? {}, digest: current?.digest, refresh } : null
  ), [current?.references, current?.digest, refresh, required]);
  return {
    context,
    loading: required && !current,
  };
};
